/**
 * Seeded RNG — Plan 08-03 (GAME-02/03/04 공통).
 *
 * 모든 게임의 결과는 `donationEventId` 를 seed 로 재현 가능해야 한다.
 * 이유: T-08-03-04 Repudiation — 게임 결과 조작 논란 시 감사관이 동일 seed 로
 * 독립적으로 결과를 재현, 서버가 조작하지 않았음을 증명.
 *
 * 구현: mulberry32 + FNV-1a string hash (pure TS, WebGL/Node 양쪽 동작).
 * Wave 0 범위에서는 암호학적 보안성 불필요 — 결정론 + 단일 라운드 공정성만 보장.
 *
 * API:
 *   const rng = createSeededRng('event-abc');
 *   rng.next(); // [0, 1) — 결정적 sequence.
 *
 * 동일 seed → 동일 first N numbers.
 * 2-step disclosure (hash start · reveal end) 는 service layer (08-04) 에서 사용 —
 * 스트리머가 라운드 시작 시 `hash(seed)` 발표, 종료 후 `seed` 공개로 감사 가능.
 */

export interface SeededRng {
  /** 다음 난수를 반환. [0, 1) 범위의 float. */
  next(): number;
}

/** FNV-1a 32-bit string hash. 결정적 · 비암호학적. */
function fnv1a(seed: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * mulberry32 — 32-bit state PRNG. 주기 2^32, 통계적 품질 우수
 * (PractRand 기준 ~8GB until failure — Wave 0 게임 라운드에 충분).
 */
export function createSeededRng(seed: string): SeededRng {
  if (typeof seed !== 'string' || seed.length === 0) {
    throw new TypeError('seed must be non-empty string');
  }
  let a = fnv1a(seed);
  return {
    next: (): number => {
      a = (a + 0x6d2b79f5) | 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    },
  };
}

/**
 * Cumulative probability 에서 index 선택 — RNG 공통 유틸.
 * - weights 합 ≈ 1.0 (Zod 에서 검증됨) 전제.
 * - r ∈ [0, 1) → cumulative[i-1] ≤ r < cumulative[i] 인 i 반환.
 */
export function pickCumulative(weights: readonly number[], r: number): number {
  let acc = 0;
  for (let i = 0; i < weights.length; i += 1) {
    acc += weights[i] ?? 0;
    if (r < acc) return i;
  }
  return weights.length - 1;
}

/**
 * Weighted index 선택 (weights 합이 임의값). r ∈ [0, 1) 에 총합 곱으로 scale.
 * 슬롯 symbol weight 용 (정규화 불필요).
 */
export function pickWeighted(weights: readonly number[], r: number): number {
  let total = 0;
  for (const w of weights) total += w;
  if (total <= 0) return 0;
  const target = r * total;
  let acc = 0;
  for (let i = 0; i < weights.length; i += 1) {
    acc += weights[i] ?? 0;
    if (target < acc) return i;
  }
  return weights.length - 1;
}
